/**
 * Profile Service
 *
 * The user's stored preferences - favorites, their order, custom links, and
 * dismissed games. All of it lived in localStorage before D22; it's now
 * server state reached over `/api/profile`.
 *
 * Note this is a different endpoint from `/auth/profile`, which answers
 * "who am I" for session restoration rather than "what has this user chosen".
 */

import { apiClient } from './apiClient';
import { ensureToken, getToken } from './tokenService';

// What a visitor with no record yet has. Also the shape every caller can
// rely on, so no component has to null-check its way through a fetch.
export const EMPTY_PROFILE = {
  favorites: [],
  favoriteOrder: [],
  customLinks: [],
  unliked: [],
};

/**
 * Fetch the current user's preferences.
 *
 * Returns the empty profile without a round trip when there's no token -
 * a visitor who hasn't written anything yet has no record to read, and
 * asking would create one.
 *
 * @returns {Promise<Object>}
 */
export const getProfile = async () => {
  if (!getToken()) return { ...EMPTY_PROFILE };

  const profile = await apiClient.get('/profile');
  return { ...EMPTY_PROFILE, ...profile };
};

/**
 * Replace the given preference fields.
 *
 * Fields left out of `changes` are untouched, so a favorite toggle can't
 * clobber a custom link added in the same moment. Fields present are
 * replaced wholesale - there is no array merging anywhere (D8).
 *
 * Creates the user record first if this is the visitor's first write.
 *
 * @param {Object} changes Any of favorites, favoriteOrder, customLinks, unliked
 * @returns {Promise<Object>} The saved profile
 */
export const saveProfile = async (changes) => {
  await ensureToken();
  const profile = await apiClient.put('/profile', changes);
  return { ...EMPTY_PROFILE, ...profile };
};
